/* eslint-disable react/jsx-indent, react/jsx-closing-tag-location */
import React from 'react'
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  alpha
} from '@mui/material'

import { qboChargeRowDedupeKey } from '../../utils/qboChargeMapping'
import { formatMonthKeyShortYear } from '../../utils/dashboardMath'
import { clientColumnColor, resolveChargeCategoryKey } from './chargesPanelUtils'

const CLIENT_ACCENT_CLASS = 'charges-panel-client-accent'

const BillingChargesInGraphTable = ({
  bill,
  accent,
  sortedRows,
  sortOrder,
  sortColumn,
  onRequestSort,
  allRowsSelectedForGraph,
  someRowsSelectedForGraph,
  onToggleSelectAllVisible,
  selectedCharges,
  excludedChargeRowKeys,
  chargesPresentInData,
  allChargeOptions,
  clientSettings,
  onToggleRowInGraph,
  headCellSx,
  sortLabelSx
}) => (
  <Box
    sx={{
      flex: { md: '1 1 0%' },
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      borderRight: { md: `1px solid ${alpha(bill.border, 0.85)}` },
      borderBottom: { xs: `1px solid ${alpha(bill.border, 0.85)}`, md: 'none' }
    }}
  >
    <TableContainer
      sx={{
        flex: 1,
        minHeight: 200,
        maxHeight: { xs: 'min(42vh, 480px)', md: 'min(52vh, 560px)' },
        width: '100%',
        overflow: 'auto'
      }}
    >
      <Table size='small' stickyHeader sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headCellSx, width: 48 }}>
              <Checkbox
                size='small'
                indeterminate={someRowsSelectedForGraph && !allRowsSelectedForGraph}
                checked={allRowsSelectedForGraph && sortedRows.length > 0}
                disabled={!sortedRows.length}
                onChange={() => onToggleSelectAllVisible?.(sortedRows, !allRowsSelectedForGraph)}
                sx={{
                  p: 0.5,
                  '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: accent }
                }}
              />
            </TableCell>
            <TableCell sx={headCellSx}>
              <TableSortLabel
                active={sortColumn === 'client'}
                direction={sortColumn === 'client' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('client')}
                sx={sortLabelSx}
              >
                Client
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headCellSx}>
              <TableSortLabel
                active={sortColumn === 'chargeType'}
                direction={sortColumn === 'chargeType' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('chargeType')}
                sx={sortLabelSx}
              >
                Charge type
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headCellSx}>
              <TableSortLabel
                active={sortColumn === 'chargeName'}
                direction={sortColumn === 'chargeName' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('chargeName')}
                sx={sortLabelSx}
              >
                Charge
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headCellSx}>
              <TableSortLabel
                active={sortColumn === 'invoice'}
                direction={sortColumn === 'invoice' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('invoice')}
                sx={sortLabelSx}
              >
                Invoice #
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headCellSx}>
              <TableSortLabel
                active={sortColumn === 'billingMonth'}
                direction={sortColumn === 'billingMonth' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('billingMonth')}
                sx={sortLabelSx}
              >
                Billing month
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>
              <TableSortLabel
                active={sortColumn === 'amount'}
                direction={sortColumn === 'amount' ? sortOrder : 'asc'}
                onClick={() => onRequestSort('amount')}
                sx={{ ...sortLabelSx, flexDirection: 'row-reverse', ml: 'auto' }}
              >
                Amount
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!sortedRows.length && (
            <TableRow>
              <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: bill.muted, border: 'none' }}>
                No charge lines for the selected clients and date range.
              </TableCell>
            </TableRow>
          )}
          {sortedRows.map((row) => {
            const rowKey = qboChargeRowDedupeKey(row)
            const catKey = resolveChargeCategoryKey(row, allChargeOptions, chargesPresentInData)
            const categoryInFilterList = Boolean(catKey && chargesPresentInData?.has?.(catKey))
            const categoryChecked = categoryInFilterList && Boolean(selectedCharges?.[catKey])
            const checkboxDisabled = !categoryInFilterList
            const rowCountsTowardGraph =
              categoryChecked && !excludedChargeRowKeys[rowKey]
            const rowSx = !categoryInFilterList
              ? {
                  opacity: 0.42,
                  [`& .MuiTableCell-root:not(.${CLIENT_ACCENT_CLASS})`]: { color: alpha(bill.text, 0.45) }
                }
              : (!categoryChecked
                  ? {
                      opacity: 0.88,
                      [`& .MuiTableCell-root:not(.${CLIENT_ACCENT_CLASS})`]: { color: alpha(bill.text, 0.78) }
                    }
                  : undefined)
            const clientColor = clientColumnColor(
              clientSettings,
              row,
              categoryInFilterList,
              categoryChecked,
              bill.text,
              alpha
            )
            const clientColorSx = { color: clientColor }
            const checkboxAccent =
              (row?.clientName && clientSettings[row.clientName]?.color) || accent

            return (
              <TableRow
                key={rowKey}
                hover={!checkboxDisabled}
                sx={rowSx}
              >
                <TableCell padding='checkbox' sx={{ borderColor: alpha(bill.border, 0.65) }}>
                  <Checkbox
                    size='small'
                    checked={rowCountsTowardGraph}
                    disabled={checkboxDisabled}
                    onChange={(e) => onToggleRowInGraph?.(row, e.target.checked)}
                    sx={{
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: checkboxAccent }
                    }}
                  />
                </TableCell>
                <TableCell
                  className={CLIENT_ACCENT_CLASS}
                  sx={{ borderColor: alpha(bill.border, 0.65), fontWeight: 600, ...clientColorSx }}
                >
                  {row.clientName || '—'}
                </TableCell>
                <TableCell sx={{ borderColor: alpha(bill.border, 0.65), color: bill.text }}>
                  {row.chargeCategory || '—'}
                </TableCell>
                <TableCell sx={{ borderColor: alpha(bill.border, 0.65), color: bill.text }}>
                  {row.chargeName || '—'}
                </TableCell>
                <TableCell sx={{ borderColor: alpha(bill.border, 0.65), color: bill.text }}>
                  {row.invoiceNumber || '—'}
                </TableCell>
                <TableCell sx={{ borderColor: alpha(bill.border, 0.65), color: bill.muted }}>
                  {row.billingMonthKey ? formatMonthKeyShortYear(row.billingMonthKey) : '—'}
                </TableCell>
                <TableCell
                  className={CLIENT_ACCENT_CLASS}
                  align='right'
                  sx={{
                    borderColor: alpha(bill.border, 0.65),
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    ...clientColorSx
                  }}
                >
                  {Number(row.amount || 0).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  })}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)

export { BillingChargesInGraphTable }
